/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-this-in-sfc */
/* eslint-disable func-names */
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import { LineBarPlotTypeSelector } from "components/charts/modern/inputs/PlotTypeSelector";
import AmericanCompanyContext from "components/company/v3/context/AmericanCompanyContext";
import type { TExecutiveOrDirector } from "components/company/v2/types";
import classNames from "components/ui/classNames";
import { ScrollArea, ScrollBar } from "components/ui/ScrollArea";
import { SheetContent, SheetDescription, SheetHeader, SheetTitle } from "components/ui/Sheet";
import aeonik from "components/utils/aeonik";
import formatMoneyValueOnlyDigits from "components/utils/formatMoneyValueOnlyDigits";
import { compareAsc, getYear, parseISO } from "date-fns";
import type Highcharts from "highcharts";
import type { SeriesOptionsType } from "highcharts";
import { useAmplitude } from "lib/amplitude/Amplitude";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { ReactNode } from "react";
import { useContext, useEffect, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import colors from "tailwindcss/colors";

interface IExecutiveOrDirectorSheetContentProps {
  issuer: IAmericanCompanyIssuerTopLevelFragment;
  person: TExecutiveOrDirector;
}
export default function ExecutivesOrDirectorSheetContent({ issuer, person }: IExecutiveOrDirectorSheetContentProps) {
  const amplitude = useAmplitude();
  const fullName = `${person.firstName} ${person.lastName}`.trim();
  useEffect(() => {
    amplitude.logEvent("Viewed executive compensation", {
      issuerId: issuer._id,
      tickerSymbol: issuer.primaryTickerSymbol,
      name: issuer.name,
      sector: issuer.sector,
      person: person.fullName,
    });
  }, [person.id]);
  return (
    <SheetContent position="right" className="h-screen w-screen space-y-4 overflow-y-scroll bg-white xl:w-1/2">
      <SheetHeader className="text-left">
        <div className="flex items-center">
          <SheetTitle className="text-3xl font-black text-slate-800">{fullName}</SheetTitle>
          {issuer.niceLogo && (
            <div className="px-4">
              <img
                role="presentation"
                src={issuer.niceLogo.url}
                width={42}
                height={42}
                alt={issuer.name}
                draggable={false}
                className="block h-10 w-10 select-none object-contain object-right squircle"
              />
            </div>
          )}
        </div>

        <h3 className="text-lg font-black text-slate-700">{person.standardTitle}</h3>

        {person.centralIndexKey && (
          <div>
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex text-sm font-bold text-blue-600 no-underline hover:underline"
              href={`https://www.sec.gov/edgar/browse/?CIK=${person.centralIndexKey}`}
            >
              View on SEC
            </a>
          </div>
        )}

        <SheetDescription asChild={true}>
          <blockquote className="rounded-md bg-slate-50 p-4 text-sm italic leading-relaxed tracking-wider text-slate-600">
            <p className="line-clamp-3">{person.biography}</p>
          </blockquote>
        </SheetDescription>
      </SheetHeader>

      <div className="space-y-1">
        <h4 className="text-xl font-bold text-slate-800">Compensation History</h4>
        <p className="text-sm italic tracking-wider text-slate-500">Last 10 years in USD (U.S. Dollar)</p>
      </div>

      <ExecutiveCompensationHistoryTable
        executiveCompensationHistory={person.executiveCompensationHistoryInUSD.slice(0, 10)}
      />

      {person.executiveCompensationHistoryInUSD.length >= 3 && (
        <ExecutivesAndDirectorsCompenstationHistoryChart
          executiveCompensationHistory={person.executiveCompensationHistoryInUSD}
        />
      )}
    </SheetContent>
  );
}

interface IExecutivesAndDirectorsCompenstationHistoryChartProps {
  executiveCompensationHistory: TExecutiveOrDirector["executiveCompensationHistoryInUSD"];
}
function ExecutivesAndDirectorsCompenstationHistoryChart({
  executiveCompensationHistory,
}: IExecutivesAndDirectorsCompenstationHistoryChartProps) {
  const [seriesType, setSeriesType] = useState<Extract<SeriesOptionsType["type"], "line" | "bar">>("line");
  const issuer = useContext(AmericanCompanyContext);
  if (!issuer) {
    return null;
  }
  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    yAxis: {
      title: {
        text: "",
      },
      gridLineColor: colors.slate[200],
      labels: {
        align: "center",
        enabled: true,
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    xAxis: {
      zoomEnabled: false,
      categories: executiveCompensationHistory.map((node) => {
        return String(getYear(parseISO(node.reportDate)));
      }),
      lineWidth: 0,
      labels: {
        align: "center",
        enabled: true,
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      crosshair: {
        color: colors.slate[300],
        width: 1,
      },
    },
    legend: {
      enabled: false,
      floating: false,
    },
    tooltip: {
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[300],
      borderWidth: 1,
      padding: 12,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      formatter() {
        return this.y && typeof this.y === "number"
          ? renderToStaticMarkup(
              <div className="space-x-2">
                <span className="font-aeonik font-bold text-slate-600">{this.x}</span>
                <span>•</span>
                <span className="font-mono font-bold">${formatMoneyValueOnlyDigits(this.y)}</span>
              </div>,
            )
          : "N/A";
      },
    },
    series: [
      {
        type: seriesType,
        animation: false,
        name: "Total Compensation",
        borderWidth: 2,
        data: executiveCompensationHistory.map((node) => {
          return [getYear(parseISO(node.reportDate)), node.calculatedTotal];
        }),
      },
    ],
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
    },
    plotOptions: {
      line: {
        color: colors.purple[600],
        lineWidth: 2,
        marker: {
          enabled: false,
          fillColor: colors.white,
          lineColor: colors.purple[600],
          lineWidth: 2,
          width: 1,
        },
      },
      bar: {
        color: colors.purple[50],
        borderColor: colors.purple[600],
        borderRadius: {
          where: "end",
          radius: 4,
          scope: "stack",
        },
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
  };
  return (
    <div className="space-y-4">
      <div className="flex">
        <LineBarPlotTypeSelector
          value={seriesType === "bar" ? "column" : "line"}
          onChange={(plotType) => {
            setSeriesType(plotType === "column" ? "bar" : "line");
          }}
        />
      </div>
      <HighchartsChart constructorType="chart" options={options} />
    </div>
  );
}

interface IToggleChartTypeButtonProps<AvailableSeriesType extends SeriesOptionsType["type"]> {
  isActive: boolean;
  seriesType: Extract<SeriesOptionsType["type"], AvailableSeriesType>;
  onSetSeriesType: (seriesType: Extract<SeriesOptionsType["type"], AvailableSeriesType>) => void;
  children: ReactNode;
}
export function ToggleChartTypeButton<AvailableSeriesType extends SeriesOptionsType["type"]>({
  isActive,
  seriesType,
  onSetSeriesType,
  children,
}: IToggleChartTypeButtonProps<AvailableSeriesType>) {
  return (
    <button
      type="button"
      className={classNames("rounded-xl bg-gray-100 p-2", {
        "bg-white text-violet-600 ring-4 ring-violet-100 ring-offset-1 ring-offset-violet-200": isActive,
      })}
      onClick={() => onSetSeriesType(seriesType)}
    >
      {children}
    </button>
  );
}

export type NullableKeys<T> = {
  [P in keyof T]-?: Extract<T[P], null | undefined> extends never ? never : P;
}[keyof T];

export type ExtractNullable<T> = {
  [P in NullableKeys<T>]: NonNullable<T[P]>;
};

interface IExecutiveCompensationHistoryTableProps {
  executiveCompensationHistory: TExecutiveOrDirector["executiveCompensationHistoryInUSD"];
}

function ExecutiveCompensationHistoryTable({ executiveCompensationHistory }: IExecutiveCompensationHistoryTableProps) {
  const hasPayoutForFn = (key: NullableKeys<TExecutiveOrDirector["executiveCompensationHistoryInUSD"][0]>) => {
    return executiveCompensationHistory.some((node) => {
      return node[key] !== null;
    });
  };
  const hasPayoutFor = {
    salary: hasPayoutForFn("salary"),
    bonus: hasPayoutForFn("bonus"),
    otherAnnualCompensation: hasPayoutForFn("otherAnnualCompensation"),
    restrictedStockAward: hasPayoutForFn("restrictedStockAward"),
    securityUnderlyingOptions: hasPayoutForFn("securityUnderlyingOptions"),
    longTermIncentivePlanPayouts: hasPayoutForFn("longTermIncentivePlanPayouts"),
    optionAward: hasPayoutForFn("optionAward"),
    nonEquityIncentiveAward: hasPayoutForFn("nonEquityIncentiveAward"),
    changeInPensionValue: hasPayoutForFn("changeInPensionValue"),
    allOtherCompensation: hasPayoutForFn("allOtherCompensation"),
    totalCompensation: hasPayoutForFn("totalCompensation"),
    maximumPerformanceSharePotential: hasPayoutForFn("maximumPerformanceSharePotential"),
  };
  return (
    <div className="inline-block min-w-full py-4 align-middle">
      <ScrollArea
        className={classNames(
          `w-[calc(100vw-theme('spacing.12'))] rounded-xl shadow-sm sm:w-full sm:border-transparent`,
          "ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-200",
          "sm:rounded-lg sm:border sm:border-slate-200 sm:shadow-none sm:ring-0 sm:ring-offset-0",
        )}
      >
        <ScrollBar orientation="horizontal" />
        <table className="min-w-full divide-y divide-slate-200">
          <thead>
            <tr className="text-right">
              <th scope="col" className="p-4 px-4 text-left text-xs font-semibold text-gray-900 sm:text-sm">
                Year
              </th>
              <th scope="col" className="p-4 px-4 text-xs font-semibold text-gray-900 sm:text-sm">
                Salary
              </th>
              {hasPayoutFor.bonus && (
                <th scope="col" className="p-4 px-4 text-xs font-semibold text-gray-900 sm:text-sm">
                  Bonus
                </th>
              )}
              {hasPayoutFor.otherAnnualCompensation && (
                <th scope="col" className="p-4 px-4 text-xs font-semibold text-gray-900 sm:text-sm">
                  Annual Other Income
                </th>
              )}
              {hasPayoutFor.restrictedStockAward && (
                <th scope="col" className="p-4 px-4 text-xs font-semibold text-gray-900 sm:text-sm">
                  Restricted Stock Award
                </th>
              )}
              {hasPayoutFor.securityUnderlyingOptions && (
                <th scope="col" className="p-4 px-4 text-xs font-semibold text-gray-900 sm:text-sm">
                  Securities Options
                </th>
              )}
              {hasPayoutFor.longTermIncentivePlanPayouts && (
                <th scope="col" className="p-4 px-4 text-xs font-semibold text-gray-900 sm:text-sm">
                  LTIP Payout
                </th>
              )}
              {hasPayoutFor.nonEquityIncentiveAward && (
                <th scope="col" className="p-4 px-4 text-xs font-semibold text-gray-900 sm:text-sm">
                  Non-Equity Compensation
                </th>
              )}
              {hasPayoutFor.allOtherCompensation && (
                <th scope="col" className="p-4 px-4 text-xs font-semibold text-gray-900 sm:text-sm">
                  Other Compensation
                </th>
              )}
              <th scope="col" className="p-4 px-4 text-xs font-semibold text-gray-900 sm:text-sm">
                Total Compensation
              </th>
            </tr>
          </thead>
          <tbody>
            {executiveCompensationHistory
              .sort((a, b) => compareAsc(parseISO(a.reportDate), parseISO(b.reportDate)))
              .map((node) => (
                <tr key={node.reportDate} className="text-right even:bg-slate-50">
                  <td className="whitespace-nowrap bg-slate-100 p-2 px-4 text-left text-xs font-bold text-gray-900 sm:text-sm">
                    {getYear(parseISO(node.reportDate))}
                  </td>
                  <td className="whitespace-nowrap p-2 px-4 font-mono text-xs font-medium text-gray-700 sm:text-sm">
                    {node.salary ? `$${formatMoneyValueOnlyDigits(node.salary)}` : "-"}
                  </td>
                  {hasPayoutFor.bonus && (
                    <td className="whitespace-nowrap p-2 px-4 font-mono text-xs font-medium text-gray-700 sm:text-sm">
                      {node.bonus ? `$${formatMoneyValueOnlyDigits(node.bonus)}` : "-"}
                    </td>
                  )}
                  {hasPayoutFor.otherAnnualCompensation && (
                    <td className="whitespace-nowrap p-2 px-4 font-mono text-xs font-medium text-gray-700 sm:text-sm">
                      {node.otherAnnualCompensation
                        ? `$${formatMoneyValueOnlyDigits(node.otherAnnualCompensation)}`
                        : "-"}
                    </td>
                  )}
                  {hasPayoutFor.restrictedStockAward && (
                    <td className="whitespace-nowrap p-2 px-4 font-mono text-xs font-medium text-gray-700 sm:text-sm">
                      {node.restrictedStockAward ? `$${formatMoneyValueOnlyDigits(node.restrictedStockAward)}` : "-"}
                    </td>
                  )}
                  {hasPayoutFor.securityUnderlyingOptions && (
                    <td className="whitespace-nowrap p-2 px-4 font-mono text-xs font-medium text-gray-700 sm:text-sm">
                      {node.securityUnderlyingOptions
                        ? `$${formatMoneyValueOnlyDigits(node.securityUnderlyingOptions)}`
                        : "-"}
                    </td>
                  )}
                  {hasPayoutFor.longTermIncentivePlanPayouts && (
                    <td className="whitespace-nowrap p-2 px-4 font-mono text-xs font-medium text-gray-700 sm:text-sm">
                      {node.longTermIncentivePlanPayouts
                        ? `$${formatMoneyValueOnlyDigits(node.longTermIncentivePlanPayouts)}`
                        : "-"}
                    </td>
                  )}
                  {hasPayoutFor.nonEquityIncentiveAward && (
                    <td className="whitespace-nowrap p-2 px-4 font-mono text-xs font-medium text-gray-700 sm:text-sm">
                      {node.nonEquityIncentiveAward
                        ? `$${formatMoneyValueOnlyDigits(node.nonEquityIncentiveAward)}`
                        : "-"}
                    </td>
                  )}
                  {hasPayoutFor.allOtherCompensation && (
                    <td className="whitespace-nowrap p-2 px-4 font-mono text-xs font-medium text-gray-700 sm:text-sm">
                      {node.allOtherCompensation ? `$${formatMoneyValueOnlyDigits(node.allOtherCompensation)}` : "-"}
                    </td>
                  )}
                  <td className="whitespace-nowrap bg-emerald-50 p-2 px-4 font-mono text-xs font-medium text-emerald-900 sm:text-sm">
                    {node.totalCompensation ? `$${formatMoneyValueOnlyDigits(node.totalCompensation)}` : "-"}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </ScrollArea>
    </div>
  );
}
